import type { UseQueryOptions, UseQueryResult } from '@tanstack/react-query'
import { useQuery } from '@tanstack/react-query'

import { QUERIES } from '@/enums/queries'
import { globalQueryClient } from '@/queries/queryClient'
import type {
  UserDetail,
  UserLabel,
  UserListResponse,
  UserParams,
} from '@/types/user'

import { getUserDetails, getUsers, getUsersLabels } from './services/loadUsers'

export const useLoadUsers = (
  params: UserParams,
  options: Omit<
    UseQueryOptions<
      UserListResponse,
      Error,
      UserListResponse,
      [string, UserParams]
    >,
    'queryKey' | 'queryFn'
  > = {}
): UseQueryResult<UserListResponse, Error> => {
  return useQuery({
    queryKey: [QUERIES.USERS.LIST, params],
    queryFn: ({ signal }) => getUsers(params, { signal }),
    ...options,
  })
}

export const useLoadUsersLabels = (
  options?: Omit<
    UseQueryOptions<UserLabel[], Error, UserLabel[], [string]>,
    'queryKey' | 'queryFn'
  >
): UseQueryResult<UserLabel[], Error> => {
  return useQuery({
    queryKey: [QUERIES.USERS.LIST_LABELS],
    queryFn: () => getUsersLabels(),
    ...options,
  })
}

export const invalidateLoadUsers = async () => {
  await globalQueryClient.invalidateQueries({
    queryKey: [QUERIES.USERS.LIST],
    exact: false,
  })

  await globalQueryClient.invalidateQueries({
    queryKey: [QUERIES.USERS.LIST_LABELS],
    exact: false,
  })
}

export const useLoadUserDetails = (
  options?: Omit<
    UseQueryOptions<UserDetail, Error, UserDetail, string[]>,
    'initialData'
  >
): UseQueryResult<UserDetail, Error> => {
  return useQuery({
    queryKey: [QUERIES.USERS.DETAILS],
    queryFn: () => getUserDetails(),
    ...options,
  })
}

export const invalidateLoadUserDetails = async () => {
  await globalQueryClient.invalidateQueries({
    queryKey: [QUERIES.USERS.DETAILS],
    exact: false,
  })
}
